import { Component, Input, ViewChild } from '@angular/core';
import { User } from 'src/app/models/user.model';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { UserService } from 'src/app/services/user.service';
import { lastValueFrom } from 'rxjs';
import { PrescriptionTemplate, PrescriptionTemplatesService } from 'src/app/services/prescription-templates.service';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { InsolesPrescriptionComponent } from '../insoles-prescription/insoles-prescription.component';
import { insoleComponentesConfig } from '../insole-component/components-config';
import Swal from 'sweetalert2';
import { InsolesPrescriptionResponse } from 'src/app/models/formResponse.model';
import { Client } from 'src/app/models/client.model';
import { Roles } from 'src/app/layouts/full/shared/Enums';

@Component({
  selector: 'app-prescription-templates',
  standalone: true,
  imports: [SharedModule, InsolesPrescriptionComponent],
  templateUrl: './prescription-templates.component.html',
  styleUrl: './prescription-templates.component.scss'
})
export class PrescriptionTemplatesComponent {
  @Input() selectedClient: Client | null = null;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator = Object.create(null);
  pageSize = 5;
  isLoading: boolean = false;
  createPrescriptionTemplateModalVisible: boolean = false;
  updatePrescriptionTemplateModalVisible: boolean = false;
  insolesPrescriptionForm: FormGroup
  selectedPrescriptionTemplate: PrescriptionTemplate | null = null;

  dataSource = new MatTableDataSource([]);
  displayedColumns: string[] = [
    'name',
    'type',
    'createdAt',
    'updatedAt',
    'createdBy',
    'actions'
  ];

  public user: User;
  public createPrescriptionTemplateForm: FormGroup
  public updatePrescriptionTemplateForm: FormGroup
  public Roles = Roles;

  constructor(
    private userService: UserService,
    private prescriptionTemplatesService: PrescriptionTemplatesService,
    private formBuilder: FormBuilder
  ) {

    this.user = userService.user

    this.createPrescriptionTemplateForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      metadata: this.formBuilder.array([])
    })

    this.updatePrescriptionTemplateForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      metadata: this.formBuilder.array([])
    })

  }

  ngOnInit(): void {
    // Si entramos como DISTRIBUTOR le pasamos selectedClient
    if (this.selectedClient) {
      this.getPrescriptionTemplates(this.selectedClient.ID);
    } else {
      this.getPrescriptionTemplates(this.user.client.id);
    }

    this.initializePrescriptionForm();
  }

  initializePrescriptionForm() {
    let insoleComponentsConfig = insoleComponentesConfig;
    let insoleComponentsForms = [];

    // Add all the components to the components prescription form
    insoleComponentsConfig.map((component) => {
      insoleComponentsForms.push(component.form)
    })

    // Create Insoles Prescription Form
    this.insolesPrescriptionForm = this.formBuilder.group({
      type: new FormControl('insolesPrescription', { nonNullable: true }),
      shellType: new FormControl('thin'),
      lengthType: new FormControl('3-4'),
      thinThickness: new FormControl(2),
      components: this.formBuilder.array(insoleComponentsForms)
    })
  }

  public prepareInsolesPrescriptionFormToSend() {

    const componentsArray = this.insolesPrescriptionForm!.get('components') as FormArray;

    // Filter only the componentes that the form is valid and has some foot selected
    // Because when nothing it's selected as "foot" it's always valid
    const validFormsWithFootSelection = componentsArray.controls.filter((formGroup: any) => {
      return formGroup.valid && formGroup.get('footSelection')!.value;
    });

    // Prepare array of components to request
    let componentsToRequest = [];

    // Add all the valid components to the components list
    validFormsWithFootSelection.forEach(formGroup => {
      componentsToRequest.push(formGroup.value)
    });

    // Map the data to request, and list only components "valid" and with Foot selected
    const insolePrescriptionForm = {
      shellType: this.insolesPrescriptionForm.get('shellType')?.value,
      lengthType: this.insolesPrescriptionForm.get('lengthType')?.value,
      thinThickness: this.insolesPrescriptionForm.get('thinThickness')?.value,
      components: validFormsWithFootSelection.map(component => component.value)
    };

    return insolePrescriptionForm
  }


  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  async getPrescriptionTemplates(clientID: string) {
    this.isLoading = true;

    this.dataSource.data = await lastValueFrom(this.prescriptionTemplatesService.getPrescriptionTemplatesByClientID(clientID))

    this.isLoading = false
  }

  async createPrescriptionTemplate() {
    this.createPrescriptionTemplateModalVisible = false

    Swal.fire({
      title: "正在创建……",
      icon: "info",
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const insolesPrescriptionForm = this.prepareInsolesPrescriptionFormToSend()
    const metadata = this.convertMetadataArrayToObject('create');

    const prescriptionTemplateData: PrescriptionTemplate = {
      name: this.createPrescriptionTemplateForm.get('name')?.value,
      description: this.createPrescriptionTemplateForm.get('description')?.value ?? "",
      type: 'Insoles',
      clientID: this.selectedClient?.ID ?? this.user.client.id,
      prescription: insolesPrescriptionForm,
      metadata: metadata,
      createdAt: Math.floor(Date.now() / 1000),
      updatedAt: Math.floor(Date.now() / 1000),
      createdBy: this.user.name,
    }

    await lastValueFrom(this.prescriptionTemplatesService.createPrescriptionTemplate(prescriptionTemplateData))

    Swal.fire({
      icon: "success",
      title: "处方模板已创建！",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true
    })

    this.getPrescriptionTemplates(this.selectedClient?.ID ?? this.user.client.id)
  }

  async updatePrescriptionTemplate() {
    this.updatePrescriptionTemplateModalVisible = false

    Swal.fire({
      title: "正在更新……",
      icon: "info",
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const insolesPrescriptionForm = this.prepareInsolesPrescriptionFormToSend()
    const metadata = this.convertMetadataArrayToObject('update');

    const prescriptionTemplateData: PrescriptionTemplate = {
      name: this.updatePrescriptionTemplateForm.get('name')?.value,
      description: this.updatePrescriptionTemplateForm.get('description')?.value ?? "",
      type: 'Insoles',
      clientID: this.selectedClient?.ID ?? this.user.client.id,
      prescription: insolesPrescriptionForm,
      metadata: metadata,
      createdAt: this.selectedPrescriptionTemplate?.createdAt,
      updatedAt: Math.floor(Date.now() / 1000),
      createdBy: this.user.name,
    }

    await lastValueFrom(this.prescriptionTemplatesService.updatePrescriptionTemplate(this.selectedPrescriptionTemplate.ID, prescriptionTemplateData))

    Swal.fire({
      icon: "success",
      title: "处方模板已更新！",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true
    })

    this.getPrescriptionTemplates(this.selectedClient?.ID ?? this.user.client.id)
  }

  getComponents() {
    return (this.selectedPrescriptionTemplate?.prescription.get('components') as FormArray)?.controls || [];
  }

  editPrescriptionTemplate(prescriptionTemplateId: any): void {
    this.isLoading = true;
    this.showUpdatePrescriptionTemplateModal();

    // Encuentra la plantilla de prescripción
    this.selectedPrescriptionTemplate = this.dataSource.data.find(
      (prescriptionTemplate) => prescriptionTemplate.ID === prescriptionTemplateId
    );

    this.updatePrescriptionTemplateForm.get('name')?.setValue(this.selectedPrescriptionTemplate?.name);
    this.updatePrescriptionTemplateForm.get('description')?.setValue(this.selectedPrescriptionTemplate?.description);

    this.initializePrescriptionForm();
    let insoleComponentsConfig = insoleComponentesConfig;

    this.selectedPrescriptionTemplate.prescription as InsolesPrescriptionResponse
    this.selectedPrescriptionTemplate.prescription.components.map((insoleComponent) => {

      let componentConfig = insoleComponentsConfig.find((insoleComponentConfig) => insoleComponentConfig.form.get('type').value === insoleComponent.type);

      if (insoleComponent.left) {
        if (insoleComponent.left.value) {
          // Check if the value exists on the list, if not, then select 'others' options and apply the values
          if (componentConfig.selectOptions) {
            let foundSelecOption = componentConfig.selectOptions.find((selectOption) => selectOption.value === insoleComponent.left.value)

            if (foundSelecOption) {
              insoleComponent.left.selectedName = insoleComponent.left.value;
            } else {
              insoleComponent.left.selectedName = "others";
              insoleComponent.left.value = insoleComponent.left.value;
            }
          }
        }

        // If contains medial and not lateral that means it has to have "position"
        if ('medial' in insoleComponent.left && !('lateral' in insoleComponent.left)) {
          insoleComponent.left.position = 'medial'
        }
        if ('lateral' in insoleComponent.left && !('medial' in insoleComponent.left)) {
          insoleComponent.left.position = 'lateral'
        }
      }

      if (insoleComponent.right) {
        if (insoleComponent.right.value) {
          // Check if the value exists on the list, if not, then select 'others' options and apply the values
          if (componentConfig.selectOptions) {
            let foundSelecOption = componentConfig.selectOptions.find((selectOption) => selectOption.value === insoleComponent.right.value)

            if (foundSelecOption) {
              insoleComponent.right.selectedName = insoleComponent.right.value;
            } else {
              insoleComponent.right.selectedName = "others";
              insoleComponent.right.value = insoleComponent.right.value;
            }
          }
        }

        // If contains medial and not lateral that means it has to have "position"
        if ('medial' in insoleComponent.right && !('lateral' in insoleComponent.right)) {
          insoleComponent.right.position = 'medial'
        }
        if ('lateral' in insoleComponent.right && !('medial' in insoleComponent.right)) {
          insoleComponent.right.position = 'lateral'
        }
      }

      const componentsArray = this.insolesPrescriptionForm.get('components') as FormArray;
      // Encuentra los grupos de formulario específicos
      const foundComponent = componentsArray.controls
        .find(fg => fg.get('type').value === insoleComponent.type) as FormGroup;

      foundComponent.patchValue(insoleComponent);

    })

    this.insolesPrescriptionForm.get('shellType')?.setValue(this.selectedPrescriptionTemplate.prescription.shellType || 'thin');
    this.insolesPrescriptionForm.get('lengthType')?.setValue(this.selectedPrescriptionTemplate.prescription.lengthType || '3-4');
    this.insolesPrescriptionForm.get('thinThickness')?.setValue(this.selectedPrescriptionTemplate.prescription.thinThickness || 2);

    // Cargar metadata existente
    const metadataArray = this.updatePrescriptionTemplateForm.get('metadata') as FormArray;
    metadataArray.clear();

    if (this.selectedPrescriptionTemplate.metadata && typeof this.selectedPrescriptionTemplate.metadata === 'object') {
      Object.entries(this.selectedPrescriptionTemplate.metadata).forEach(([key, value]: [string, any]) => {
        metadataArray.push(this.formBuilder.group({
          key: [key, Validators.required],
          value: [value, Validators.required]
        }));
      });
    }

    this.isLoading = false;
  }

  showCreatePrescriptionTemplateModal(): void {
    this.insolesPrescriptionForm.reset();
    this.createPrescriptionTemplateForm.reset();

    // Limpiar metadata
    const metadataArray = this.createPrescriptionTemplateForm.get('metadata') as FormArray;
    metadataArray.clear();

    this.createPrescriptionTemplateModalVisible = true;
  }

  onCreatePrescriptionTemplateModalClose(): void {
    this.createPrescriptionTemplateModalVisible = false;
  }

  showUpdatePrescriptionTemplateModal(): void {
    this.updatePrescriptionTemplateModalVisible = true;
  }

  onUpdatePrescriptionTemplateModalClose(): void {
    this.insolesPrescriptionForm.reset();
    this.selectedPrescriptionTemplate = null;

    // Limpiar metadata
    const metadataArray = this.updatePrescriptionTemplateForm.get('metadata') as FormArray;
    metadataArray.clear();

    this.updatePrescriptionTemplateModalVisible = false;
  }

  addMetadataField(formName: 'create' | 'update'): void {
    const form = formName === 'create' ? this.createPrescriptionTemplateForm : this.updatePrescriptionTemplateForm;
    const metadataArray = form.get('metadata') as FormArray;
    metadataArray.push(this.formBuilder.group({
      key: ['', Validators.required],
      value: ['', Validators.required]
    }));
  }

  removeMetadataField(formName: 'create' | 'update', index: number): void {
    const form = formName === 'create' ? this.createPrescriptionTemplateForm : this.updatePrescriptionTemplateForm;
    const metadataArray = form.get('metadata') as FormArray;
    metadataArray.removeAt(index);
  }

  getMetadataArray(formName: 'create' | 'update'): FormArray {
    const form = formName === 'create' ? this.createPrescriptionTemplateForm : this.updatePrescriptionTemplateForm;
    return form.get('metadata') as FormArray;
  }

  private convertMetadataArrayToObject(formName: 'create' | 'update'): { [key: string]: string } {
    const metadataArray = this.getMetadataArray(formName);
    const metadataObject: { [key: string]: string } = {};

    metadataArray.controls.forEach((control: AbstractControl) => {
      const key = control.get('key')?.value;
      const value = control.get('value')?.value;
      if (key && value) {
        metadataObject[key] = value;
      }
    });

    return metadataObject;
  }
}
